import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FieldGroupMetaData, FieldMetaData, FieldsData } from '@models/checkout.model';
import { Notification } from '@models/notifications.model';
import {
  ExistingAssetDetails,
  FinalizeShoppingCartData,
  OrderDetailsAdditionalInfo,
  OrderDetailsSummary,
  ShoppingCartData,
  ShoppingCartItem
} from '@models/shopping-cart.model';
import { FieldErrorMessage, ValidateFields } from '@models/shopping.model';
import { ActivityManagerService } from '@services/activity/manager/activity-manager.service';
import { CustomFormService } from '@services/custom-form/custom-form.service';
import { SessionService } from '@services/session/session.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { CustomFormComponent } from '@shared-components/custom-form/custom-form.component';
import { PriceSummaryComponent } from '@shared-components/price-summary/price-summary.component';
import { Subject, Subscription, startWith } from 'rxjs';
import {
  DataLoaderCaptureOverrideDirective,
  DataLoaderDirective,
  DataLoaderService,
  NotificationsComponent,
  ViewService
} from 'tangoe-design-system';

import { CartItemsComponent } from '../cart-items/cart-items.component';
import { SelectedAssetComponent } from '../selected-asset/selected-asset.component';

@Component({
  selector: 'ts-finalize-order',
  templateUrl: './finalize-order.component.html',
  hostDirectives: [DataLoaderCaptureOverrideDirective],
  imports: [
    CommonModule,
    DataLoaderDirective,
    NotificationsComponent,
    SelectedAssetComponent,
    CartItemsComponent,
    CustomFormComponent,
    PriceSummaryComponent
  ]
})
export class FinalizeOrderComponent implements OnDestroy {
  form: FormGroup;
  selected_asset_form_valid: boolean = true;
  bulk_form_valid: boolean = false;
  json_form_data: FieldsData['groups'];
  data_loader_key: string;
  validation_error_notifications: Notification[];
  validation_errors: FieldErrorMessage[];
  shopping_cart: ShoppingCartData;
  shopping_items_price: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0
  };
  is_bulk_order: boolean = false;
  refresh_summary: Subject<string | boolean> = new Subject<string | boolean>();
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_manager_svc: ActivityManagerService,
    private custom_form_svc: CustomFormService,
    private data_loader_svc: DataLoaderService,
    private session_svc: SessionService,
    private shopping_svc: ShoppingService,
    private view_svc: ViewService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get disable_order_submit(): boolean {
    if (this.is_bulk_order)
      return this.form && this.form.valid && this.selected_asset_form_valid && this.bulk_form_valid
        ? false
        : true;
    else return this.form && this.form.valid && this.selected_asset_form_valid ? false : true;
  }

  get content_loading(): boolean {
    return this.data_loader_svc.isLoading(this.data_loader_key || 'global' || 'cart-prices');
  }

  get asset_details(): ExistingAssetDetails {
    return {
      existing_asset: this.shopping_cart.existing_asset!,
      keep_plan: this.shopping_cart.keep_plan
    };
  }

  get additional_info(): OrderDetailsAdditionalInfo {
    return {
      sim_options: {
        device_identifier: this.shopping_cart.device_identifier,
        order_sim: this.shopping_cart.order_sim,
        new_sim: this.shopping_cart.new_sim,
        esim_activation: this.shopping_cart.esim_activation,
        new_eid: this.shopping_cart.new_eid
      },
      travel_details: this.shopping_cart.travel_details,
      transfer_of_liability_attributes: this.shopping_cart.transfer_of_liability_attributes
    } as OrderDetailsAdditionalInfo;
  }

  get hide_price_content(): boolean {
    if (this.shopping_cart && [4, 6, 7, 8, 9, 10, 13, 15].includes(this.shopping_cart.orderTypeId))
      return false;
    return true;
  }

  get hide_one_time_cost(): boolean {
    if (this.shopping_cart && [11].includes(this.shopping_cart.orderTypeId)) return false;
    return true;
  }

  get contact_information(): FinalizeShoppingCartData['contact_information'] {
    return {
      id: (this.shopping_cart.order_for_person?.id ?? this.session_svc.getUser()?.id)!,
      email: (this.shopping_cart.order_for_person?.email ?? this.session_svc.getUser()?.email)!,
      person_name:
        this.shopping_cart.order_for_person?.person_name ?? this.session_svc.getUserFullName()!
    };
  }

  get is_valid_cart(): boolean {
    return this.activity_manager_svc.state().isValidCart(this.shopping_cart);
  }

  getShoppingCartData(cart_data: ShoppingCartData): void {
    if (cart_data) {
      this.shopping_cart = cart_data;
      this.shopping_items_price.orderTypeId = this.shopping_cart.orderTypeId;
      this.shopping_items_price.lineId = this.shopping_cart.lineId ?? undefined;
      this.updateFields(this.generateParam());
    }
  }

  updateShoppingCartData(event: string | boolean): void {
    if (event) this.refresh_summary.next(event);
  }

  onContinueSuccess(event: boolean): void {
    if (event) {
      this.shopping_cart.shopping_cart_items.forEach((item: ShoppingCartItem) => {
        item.additional_info!.unchanged = this.shopping_cart.keep_plan;
      });
      this.subscriptions.add(
        this.shopping_svc.validateFields(this.generateParam()).subscribe({
          next: (res: ValidateFields) => {
            if (res.valid) {
              this.validation_errors = [];
              this.placeOrder();
              return;
            }

            if (!res.valid) {
              this.view_svc.resetScrollPosition();
              this.validation_errors = [...(res.error_messages ?? [])];
              this.validation_error_notifications =
                this.custom_form_svc.validationNotificationsBanner(res);
              this.json_form_data = this.custom_form_svc.setFormErrors(this.json_form_data, res);
            }
          },
          error: (err: HttpErrorResponse) => {}
        })
      );
    }
  }

  isFormValid(e: boolean): void {
    this.selected_asset_form_valid = e;
  }

  isBulkFormValid(e: { valid: boolean; qty?: number }): void {
    this.bulk_form_valid = e.valid;

    if (this.bulk_form_valid) {
      this.shopping_items_price.quantity = this.shopping_cart.quantity = e.qty;
      this.refresh_summary.next(true);
    }
  }

  isBulkOrder(e: boolean): void {
    this.is_bulk_order = e;
  }

  triggerUpdateFields(e: { cart: ShoppingCartData; key?: string; update_cart?: boolean }): void {
    if (e) {
      this.shopping_cart = e.cart;
      this.updateFields(this.generateParam(e.cart.on_change_field), e.key, e.update_cart);
    }
  }

  private updateFields(
    param: FinalizeShoppingCartData,
    data_loader_key?: string,
    update_cart?: boolean,
    focus_element?: HTMLElement | null
  ): void {
    this.data_loader_key = data_loader_key ?? 'order-fields';

    this.subscriptions.add(
      this.shopping_svc.getFields(param, data_loader_key).subscribe({
        next: (res: FieldsData) => {
          this.json_form_data = res.groups;
          this.validation_error_notifications = [];
          this.activity_manager_svc.state().setSteps(res.progress_bar_config.steps);
          this.form = this.custom_form_svc.createForm(this.json_form_data);
          this.json_form_data.forEach((group: FieldGroupMetaData) => {
            group.fields.forEach((field: FieldMetaData) => {
              if (field.on_change) {
                this.subscriptions.add(
                  field.control.valueChanges.subscribe({
                    next: () => {
                      this.updateFieldsValue();
                      this.updateFields(
                        this.generateParam({ id: field.id, name: field.name }),
                        `order-fields-${group.name}`,
                        undefined,
                        document.getElementById(`${field.name}-${field.id}`)
                      );
                    }
                  })
                );
              }
            });
          });

          if (focus_element) focus_element.scrollIntoView({ behavior: 'smooth', block: 'center' });

          this.subscriptions.add(
            this.form.valueChanges
              .pipe(startWith(this.updateFieldsValue()))
              .subscribe({ next: () => this.updateFieldsValue() })
          );

          if (update_cart)
            this.subscriptions.add(
              this.shopping_svc.sendShoppingItems(this.shopping_cart).subscribe()
            );
        }
      })
    );
  }

  private updateFieldsValue(): void {
    this.shopping_cart.fields = this.custom_form_svc.parseFieldValue(this.json_form_data);
  }

  private generateParam(
    on_change_field?: FinalizeShoppingCartData['on_change_field']
  ): FinalizeShoppingCartData {
    if ([14].includes(this.shopping_cart.orderTypeId)) this.shopping_cart.lineId = undefined;

    return {
      ...this.shopping_cart,
      contact_information: this.contact_information,
      on_change_field: on_change_field
    };
  }

  private placeOrder(): void {
    this.subscriptions.add(
      this.shopping_svc
        .placeOrder(this.generateParam(), this.shopping_cart.store_order_id)
        .subscribe({
          next: (res: OrderDetailsSummary) => {
            this.activity_manager_svc.navigateToReview(res.id);
          },
          error: (err: HttpErrorResponse) => {}
        })
    );
  }
}
